import { h } from 'preact';
import { useEffect, useRef, useState, useMemo } from 'preact/hooks';
import Media from 'utils/media';
import clsx from 'clsx';
import ArrowIcon from 'utils/icon/icon-arrow.svg';
import usePhotoSwipe from '../hooks/use-photoswipe';
import { throttle } from '../utils/global-utilities';

const ProductGalleryDesktop = ({ currentProduct: { media, title}}) => {
  const [expanded, setExpanded] = useState(false);
  const isMounted = useRef(false);
  const mediaRefs = useRef([]);
  const container = useRef(null);
  const thumbs = useRef(null);
  const initialVisibleItems = 3;

  const { lightboxRef, useDataSource } = usePhotoSwipe('.pswp-gallery', media);

  const openPhotoSwipeAt = (imageIndex) => {
    if (typeof imageIndex !== 'number') return;
    lightboxRef.current?.loadAndOpen?.(imageIndex);
  };

  const updateHeight = () => {
    if (mediaRefs.current?.[4]) {
      let maxHeight = mediaRefs.current[2].parentElement.offsetHeight;
      if (!expanded) {
        maxHeight =
          mediaRefs.current[2].offsetTop + mediaRefs.current[2].offsetHeight;
      }
      container.current.style.maxHeight = `${maxHeight}px`;
    }
  };

  const scrollProduct = () => {
    const fullHeader = document.querySelector('[data-target="full-header"]');
    const fullHeaderHeight = fullHeader ? fullHeader.offsetHeight : 58;
    const viewportHeightNoHeader = window.innerHeight - fullHeaderHeight;
    if (!thumbs.current) return;
    thumbs.current.style.height = `${viewportHeightNoHeader}px`;
    const galleryContainer = container.current.closest('[data-target="gallery-container"]');
    const docTopScroll = window.pageYOffset || 
      document.documentElement.scrollTop;
    if (galleryContainer) {
      const galleryContainerHeight = galleryContainer.offsetHeight;
      const maxTop = galleryContainerHeight - viewportHeightNoHeader;
      thumbs.current.style.top = 
        `${(docTopScroll < maxTop) ? docTopScroll : maxTop}px`;
    }

    const formColumnContainer = document.querySelector('[data-target="form-column-container"]');
    if (formColumnContainer) {
      if (galleryContainer.offsetHeight > formColumnContainer.offsetHeight) {
        if (formColumnContainer.offsetHeight < viewportHeightNoHeader) {
          const widthBeforeFixed = formColumnContainer.offsetWidth;
          const parentLeftPosition = 
            formColumnContainer.parentNode.getBoundingClientRect().left;
          const leftSpace = parentLeftPosition + 
            (formColumnContainer.parentNode.offsetWidth - widthBeforeFixed) 
              / 2; 
          formColumnContainer.style.position = "fixed";
          formColumnContainer.style.top = `${ fullHeaderHeight }px`;
          formColumnContainer.style.bottom = "auto";
          formColumnContainer.style.width = 
            `${ widthBeforeFixed }px`;
            formColumnContainer.style.left = 
            `${ leftSpace }px`;
        }
        if (docTopScroll > 
          formColumnContainer.offsetHeight - viewportHeightNoHeader && 
          formColumnContainer.offsetHeight >= viewportHeightNoHeader) {

          const widthBeforeFixed = formColumnContainer.offsetWidth;
          const parentLeftPosition = 
            formColumnContainer.parentNode.getBoundingClientRect().left;
          const leftSpace = parentLeftPosition + 
            (formColumnContainer.parentNode.offsetWidth - widthBeforeFixed) 
              / 2; 
          formColumnContainer.style.position = "fixed";
          formColumnContainer.style.top = "auto";
          formColumnContainer.style.bottom = 0;
          formColumnContainer.style.width = 
            `${ widthBeforeFixed }px`;
          formColumnContainer.style.left = 
            `${ leftSpace }px`;
        } 
        if ((docTopScroll > 
          (galleryContainer.offsetHeight - viewportHeightNoHeader) && 
          formColumnContainer.offsetHeight >= viewportHeightNoHeader) || 
          (docTopScroll > (galleryContainer.offsetHeight - 
          formColumnContainer.offsetHeight) && 
          formColumnContainer.offsetHeight < viewportHeightNoHeader)) {

          formColumnContainer.removeAttribute('style');
          const widthBeforeFixed = formColumnContainer.offsetWidth;
          const sidesSpace = 
            (formColumnContainer.parentNode.offsetWidth - widthBeforeFixed) 
              / 2; 
          formColumnContainer.style.width = `${ widthBeforeFixed }px`;
          formColumnContainer.style.position = "absolute";
          formColumnContainer.style.top = 
            `${ galleryContainer.offsetHeight - 
              formColumnContainer.offsetHeight }px`;   
          formColumnContainer.style.left = 
          `${ sidesSpace }px`;               
        }
        if (docTopScroll <= 
          (formColumnContainer.offsetHeight - viewportHeightNoHeader)) {
          formColumnContainer.removeAttribute('style');
        }
      } else {
        formColumnContainer.removeAttribute('style');
      }
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    setExpanded(false);
    updateHeight();
    scrollProduct();
    window.addEventListener('scroll', scrollProduct);
    window.addEventListener('resize', scrollProduct);

    // Create a new ResizeObserver because window resize is not triggered when the height of the gallery changes
    const resizeObserver = new ResizeObserver(() => {
      scrollProduct();
    });
    resizeObserver.observe(document.body);

    return () => {
      window.removeEventListener('scroll', scrollProduct);
      window.removeEventListener('resize', scrollProduct);
    };
  }, [media]);

  useEffect(() => {
    updateHeight();
    if (isMounted.current) {
      if (!expanded) {
        mediaRefs.current[2].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    } else {
      isMounted.current = true;
    }
    const handleResize = throttle(() => updateHeight(), 400);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [expanded]);

  const expandGallery = () => {
    const galleryContainer = container.current.closest('[data-target="gallery-container"]');
    galleryContainer.classList.remove('overflow-hidden');
    galleryContainer.classList.add('overflow-auto');
    setExpanded(true);
  }

  const handleClick = (index) => {
    if (!container.current) return;
    if (!expanded) expandGallery(true);

    const scrollToIndex = () => {
      const target = container.current?.querySelectorAll('[data-index]')?.[index];
      if (!target) return;
      const scrollTopValue = target.offsetTop + 1;
      window.scrollTo({
        top: scrollTopValue,
        left: 0,
        behavior: 'smooth',
      });
    };

    // Wait for the DOM to reflect expanded state without an arbitrary delay.
    requestAnimationFrame(() => requestAnimationFrame(scrollToIndex));
  };

  const observer = useMemo(
    () =>
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const { index } = entry.target.dataset;
          if (entry.intersectionRatio > 0) {
            thumbs.current?.childNodes[0]?.childNodes[index].classList.add('active');
            thumbs.current?.childNodes[0]?.childNodes[index].querySelector('button').classList.add('active');
            thumbs.current?.childNodes[0]?.childNodes[index].querySelector('button').setAttribute('aria-current', 'true');
          } else {
            thumbs.current?.childNodes[0]?.childNodes[index].classList.remove('active');
            thumbs.current?.childNodes[0]?.childNodes[index].querySelector('button').classList.remove('active');
            thumbs.current?.childNodes[0]?.childNodes[index].querySelector('button').setAttribute('aria-current', 'false');
          }
        });
      }, {rootMargin: "-30%"}),
    [],
  );

  useEffect(() => {
    if (container.current) {
      container.current.querySelectorAll('[data-index]').forEach((element) =>
        observer.observe(element),
      );
    }

    return () => {
      observer.disconnect();
    };
  }, [observer, container, media]);

  useEffect(() => {
    scrollProduct();
  }, [thumbs]);

  return (
    <div role="region" aria-label="Carousel">
         <span className="sr-only">This is a carousel. Scroll below to view all images or use the navigation buttons to go to the corresponding slide.</span>
      <div
        ref={thumbs}
        className="hidden md:!flex z-10 absolute h-full left-5 top-0  flex-col justify-center overflow-hidden [&_.active+.active>*]:border-transparent"
      >
        <ul className='flex flex-col gap-[2px] p-1.5 bg-white'>
          {media?.length > 0 && media.map((item, index) => {
            const totalMedia = media.length;
            const position = index + 1;
            const mediaType = item.mediaContentType === 'VIDEO' ? 'video' : 'image';
            const ariaLabel = item.mediaContentType === 'VIDEO' 
              ? `${title} ${mediaType} ${position} of ${totalMedia}`
              : `${title} ${mediaType} ${position} of ${totalMedia}`;
            
            return (
              <li key={item.previewImage.id} className='flex items-center [&.active>*]:border-black'>
                <button
                  key={item.previewImage.id}
                  className=" border border-transparent cursor-pointer relative w-[34px] h-[34px]"
                  onClick={() => handleClick(index)}
                  onKeyDown={() => handleClick(index)}
                  tabIndex={0}
                  aria-label={ariaLabel}
                  role="button"
                > 
                  <Media
                    {...item}
                    className="object-cover h-full w-full"
                    parentClass="pointer-events-none h-full w-full"
                    controlClass="!opacity-100 z-10 absolute left-[2px] top-[2px] w-7 h-7 rounded-full flex items-center justify-center pointer-events-none"
                    controlIndex="-1"
                    justPlayButton="true"
                    lazyLoad={index > 1}
                    srcWidths={{
                      sm: 64,
                    }}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="relative">
        <div
          className={clsx('ease-linear duration-300 overflow-hidden', {
            'max-h-full': media?.length > 0 && media.length <= initialVisibleItems,
          })}
          ref={container}
          data-hide-scrollbar="true"
        >
          <ul className="grid lg:gap-3 relative pswp-gallery">
            {media?.length > 0 && media.map((item, index) => {
              const imageIndex = item.mediaContentType === 'IMAGE'
                ? media.slice(0, index).filter((m) => m.mediaContentType === 'IMAGE').length
                : null;
              return (
                <li
                  key={item.previewImage.id}
                  ref={(el) => {
                    mediaRefs.current[index] = el;
                  }}
                  className={`relative pb-[100%]`}
                  data-index={index}
                >
                  <Media
                    {...item}
                    className="absolute inset-0 object-cover h-full w-full"
                    controlClass="text-black bg-white !opacity-100 "
                    lazyLoad={index > 1}
                    fetchPriority={index === 0 ? 'high' : undefined}
                    alt={item?.sources?.length && item.sources[0].alt}
                    srcWidths={{
                      sm: 640,
                      md: 1200,
                      xl: 2000,
                    }}
                    enablePhotoSwipe={true}
                    onClick={
                      useDataSource && imageIndex !== null
                        ? () => openPhotoSwipeAt(imageIndex)
                        : undefined
                    }
                  />
                </li>
              );
            })}
          </ul>
        </div>
        {media?.length > initialVisibleItems && (
          <div
            onClick={() => expandGallery()}
            className={clsx('flex items-end justify-center cursor-pointer', {
              'h-10 absolute bottom-0 left-0 w-full bg-gradient-to-t from-white pb-9 pt-[181px]':
                !expanded,
              'hidden': expanded,
            })}
          >
            <button
              className='flex items-center justify-center w-10 h-10 bg-white rounded-full'
              aria-label="Show more images"
            >
              <ArrowIcon
                className='rotate-90'
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductGalleryDesktop;
